import React, {useEffect, useRef} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {
  getHotelEntities,
  getResolvedAnchorHotelId,
  getSearchId,
  getSearchParameters
} from 'modules/sapiSearch/selectors'

import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Team
} from '@vio/core/src/analytics/types/Events'
import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'

import {SimilarHotelsCarousel} from './SimilarHotelsCarousel'
import {useSimilarHotels} from './useSimilarHotels'

const Recommendations = () => {
  const {recommendations, isLoading, anchorAvailable} = useSimilarHotels()
  const anchorId = useSelector(getResolvedAnchorHotelId)
  const anchor = useSelector(getHotelEntities)?.[anchorId]
  const searchId = useSelector(getSearchId)
  const dispatch = useDispatch()
  const displayedSearchId = useRef<string>()

  useEffect(() => {
    if (
      isLoading ||
      !recommendations.length ||
      !searchId ||
      displayedSearchId.current === searchId
    )
      return
    // Store the search ID so a new search can record a display event without remounting.
    displayedSearchId.current = searchId

    dispatch(
      trackEvent({
        category: Category.System,
        entity: Entity.Carousel,
        action: Action.Displayed,
        component: 'similarHotels',
        team: Team.Default,
        payload: {
          numHotels: recommendations.length
        }
      })
    )
  }, [dispatch, isLoading, recommendations, searchId])

  return (
    <SimilarHotelsCarousel
      recommendations={recommendations}
      isLoading={isLoading}
      anchorAvailable={anchorAvailable}
      anchorName={anchor?.hotelName}
      onCardClick={(recommendation, position) =>
        dispatch(
          trackEvent({
            category: Category.User,
            entity: Entity.HotelCard,
            action: Action.Clicked,
            component: 'similarHotels',
            team: Team.Default,
            payload: {
              label: recommendation.badge ?? '',
              hotelId: recommendation.hotel.objectID,
              hotelPosition: position + 1
            }
          })
        )
      }
    />
  )
}

/**
 * Show the carousel only for eligible anchor searches assigned to the experiment treatment.
 */
export const AnchorSimilarHotels = () => {
  const toggle = useToggle()
  const search = useSelector(getSearchParameters)
  const eligible = Boolean(
    search?.hotelId && search.checkIn && search.checkOut && search.rooms
  )

  // Gate before evaluating the flag so destination searches do not record exposure.
  if (!eligible) return null

  return toggle('fec27873-similar-hotels', false, true) ? (
    <Recommendations />
  ) : null
}
